import { Box, Skeleton, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { GlowCard, SectionHeader, StatTile } from '@norvix/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { dashboardApi } from '../api/dashboard.api.js';
import { roleLabel } from '../auth/capabilities.js';
import { useAuth } from '../auth/useAuth.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { APP_LOCALE } from '../utils/locale.js';

function formatCents(cents: string, currency = 'EUR'): string {
  try {
    const n = BigInt(cents);
    const negative = n < 0n;
    const abs = negative ? -n : n;
    const major = abs / 100n;
    const minor = (abs % 100n).toString().padStart(2, '0');
    const formatted = major.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const symbol = currency === 'EUR' ? '€' : currency;
    return `${negative ? '-' : ''}${symbol} ${formatted}.${minor}`;
  } catch {
    return cents;
  }
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function DashboardPage(): JSX.Element {
  const { user } = useAuth();
  const overview = useQuery({
    queryKey: ['dashboard', 'overview'],
    queryFn: () => dashboardApi.overview(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const data = overview.data;
  const loading = overview.isLoading;
  const error = overview.isError ? (overview.error instanceof Error ? overview.error.message : 'unknown error') : null;

  const ledgerLabel = useMemo(() => {
    if (data === undefined) return '—';
    return formatCents(data.ledger.settledTodayCents, data.ledger.currency);
  }, [data]);

  return (
    <Box>
      <SectionHeader
        eyebrow="Overview"
        title={`Welcome${user !== null ? `, ${user.name}` : ''}`}
        description={
          data !== undefined
            ? `Operations across customers, compliance, ledger and risk. Snapshot at ${new Date(data.generatedAt).toLocaleTimeString(APP_LOCALE)} — auto-refresh every minute.`
            : 'Operations across customers, compliance, ledger and risk. Live metrics refresh every minute.'
        }
      />

      {error !== null && (
        <Typography color="error" sx={{ mb: 2 }}>
          Failed to load dashboard: {error}
        </Typography>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {loading ? <Skeleton variant="rounded" height={132} /> : (
            <StatTile
              label="Customers (KYC)"
              value={data?.kyc.total.toLocaleString(APP_LOCALE) ?? '—'}
              {...(data ? { hint: `${String(data.kyc.active)} active` } : {})}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {loading ? <Skeleton variant="rounded" height={132} /> : (
            <StatTile
              label="Pending KYC review"
              value={data ? String(data.kyc.pending + data.kyc.underReview) : '—'}
              {...(data ? { hint: `${String(data.kyc.underReview)} under review · ${String(data.kyc.pending)} pending` } : {})}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {loading ? <Skeleton variant="rounded" height={132} /> : (
            <StatTile
              label="Open AML alerts"
              value={data?.aml.openAlerts.toString() ?? '—'}
              hint={data && data.aml.openAlerts === 0 ? 'all clear' : 'requires review'}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {loading ? <Skeleton variant="rounded" height={132} /> : (
            <StatTile
              label="Incoming to release"
              value={data?.inbound.awaitingReview.toString() ?? '—'}
              hint={
                data && data.inbound.awaitingReview === 0
                  ? 'nothing waiting'
                  : 'customers are waiting on this money'
              }
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {loading ? <Skeleton variant="rounded" height={132} /> : (
            <StatTile
              label="Settled today"
              value={ledgerLabel}
              {...(data ? { hint: `${String(data.accounts.total)} accounts open` } : {})}
            />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <GlowCard accent="aurora" elevated>
            <Stack spacing={2}>
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                Recent onboardings
              </Typography>
              {data === undefined ? (
                <Stack spacing={1}>
                  {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="text" height={32} />)}
                </Stack>
              ) : data.kyc.recentSubmissions.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No customers have signed up yet.
                </Typography>
              ) : (
                <Stack divider={<Box sx={{ borderBottom: '1px solid var(--hairline)' }} />}>
                  {data.kyc.recentSubmissions.map((row) => {
                    return (
                      <Stack
                        key={row.customerId}
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ py: 1.2 }}
                      >
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            component={RouterLink}
                            to={`/customers/${row.customerId}`}
                            sx={{ color: 'text.primary', textDecoration: 'none', fontWeight: 600 }}
                          >
                            {row.displayName ?? row.maskedName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.muted', display: 'block', fontFamily: 'var(--font-mono)' }}>
                            {row.customerId.slice(0, 8)}…
                          </Typography>
                        </Box>
                        <StatusBadge kind="kyc" value={row.status} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 70, textAlign: 'right' }}>
                          {formatRelative(row.submittedAt)}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          </GlowCard>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>
            <GlowCard>
              <Stack spacing={1.5}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  Service health
                </Typography>
                {data?.services.map((s) => (
                  <Stack key={s.name} direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{
                      width: 8, height: 8, borderRadius: '50%',
                      bgcolor: s.status === 'OK' ? 'var(--color-success)' : 'var(--color-warning)',
                    }} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>{s.name}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'var(--font-mono)' }}>
                      {s.slo}
                    </Typography>
                  </Stack>
                ))}
                {data?.reconciliation.lastRun !== null && data !== undefined && (
                  <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid var(--hairline)' }}>
                    <Typography variant="caption" sx={{ color: 'text.muted', display: 'block' }}>
                      Last EOD reconciliation
                    </Typography>
                    <Typography variant="body2">
                      {data.reconciliation.lastRun.runDate} · <strong>{data.reconciliation.lastRun.status}</strong> · break {formatCents(data.reconciliation.lastRun.breakCents)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </GlowCard>

            <GlowCard>
              <Stack spacing={1.5}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  Your access
                </Typography>
                <Typography variant="h4">
                  {roleLabel(user?.role)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Signed in as {user?.email ?? 'anonymous'}.
                </Typography>
              </Stack>
            </GlowCard>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
